import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Sidebar from '../components/Sidebar';
import { useSidebarState } from '../hooks/useSidebarState';
import { User, Mail, Save, Loader2, Cpu, Sparkles, Zap, Check, Globe, Lock, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { apiGet, apiPut, apiPost } from '../lib/apiClient';

const Settings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const sidebarCollapsed = useSidebarState();
  const { language, setLanguage, t, availableLanguages } = useLanguage();
  const [name, setName] = useState(user?.name || '');
  const [loading, setLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState('gemini-flash');
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsLimit, setCreditsLimit] = useState(100);
  const [orgPolicy, setOrgPolicy] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);

  const fetchModels = useCallback(async () => {
    try {
      const data = await apiGet('/api/models');
      if (data) {
        setModels(data.models || []);
        setCurrentModel(data.current_model || 'gemini-flash');
        setCreditsUsed(data.credits_used || 0);
        setCreditsLimit(data.credits_limit || 100);
      }
    } catch {
      console.error('Failed to fetch models');
    }
  }, []);

  const fetchOrgModelPolicy = useCallback(async () => {
    try {
      const data = await apiGet('/api/org/model-policy');
      if (data) setOrgPolicy(data);
    } catch {}
  }, []);

  const fetchNotificationPrefs = useCallback(async () => {
    try {
      const data = await apiGet('/api/notifications/preferences');
      if (data) setNotifPrefs(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchModels();
    fetchOrgModelPolicy();
    fetchNotificationPrefs();
  }, [fetchModels, fetchOrgModelPolicy, fetchNotificationPrefs]);

  const updateNotifPref = async (key, value) => {
    setNotifLoading(true);
    const updated = { ...notifPrefs.preferences, [key]: value };
    try {
      await apiPut('/api/notifications/preferences', {
        preferences: updated,
        email_enabled: notifPrefs.email_enabled,
        in_app_enabled: notifPrefs.in_app_enabled,
      });
      setNotifPrefs(prev => ({ ...prev, preferences: updated }));
      toast.success('Notification preference updated');
    } catch { toast.error('Failed to update'); }
    finally { setNotifLoading(false); }
  };

  const toggleNotifChannel = async (channel, value) => {
    setNotifLoading(true);
    const body = {
      preferences: notifPrefs.preferences,
      email_enabled: notifPrefs.email_enabled,
      in_app_enabled: notifPrefs.in_app_enabled,
      [channel]: value,
    };
    try {
      await apiPut('/api/notifications/preferences', body);
      setNotifPrefs(prev => ({ ...prev, [channel]: value }));
      toast.success('Updated');
    } catch { toast.error('Failed to update'); }
    finally { setNotifLoading(false); }
  };

  const handleModelChange = async (modelId) => {
    setModelLoading(true);

    try {
      await apiPost(`/api/models/set-preference?model_id=${modelId}`);
      setCurrentModel(modelId);
      const updatedUser = { ...user, preferred_model: modelId };
      localStorage.setItem('decoded_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success(`Model preference updated to ${models.find(m => m.id === modelId)?.name || modelId}`);
    } catch {
      toast.error('Failed to update model preference');
    } finally {
      setModelLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    // Simulated save - in production would update via API
    setTimeout(() => {
      const updatedUser = { ...user, name };
      localStorage.setItem('decoded_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success('Settings saved successfully');
      setLoading(false);
    }, 500);
  };

  const getModelIcon = (modelId) => {
    switch (modelId) {
      case 'gemini-flash': return <Zap className="w-5 h-5" />;
      case 'gpt-4o': return <Sparkles className="w-5 h-5" />;
      case 'claude-sonnet': return <Cpu className="w-5 h-5" />;
      default: return <Cpu className="w-5 h-5" />;
    }
  };

  const getModelColor = (modelId) => {
    switch (modelId) {
      case 'gemini-flash': return 'from-blue-500 to-cyan-500';
      case 'gpt-4o': return 'from-emerald-500 to-green-500';
      case 'claude-sonnet': return 'from-orange-500 to-amber-500';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} setUser={setUser} activePage="settings" />
      
      <main className="flex-1 p-6 lg:p-8 lg:pt-24 transition-all duration-300">
        <div className="max-w-2xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-outfit text-3xl font-bold text-[#0B3D91]" data-testid="settings-title">
              User Preferences
            </h1>
            <p className="text-slate-600">
              Manage your personal preferences and AI model settings
            </p>
          </div>

          {/* AI Model Preference Section */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]">
                  AI Model Preference
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Choose your default AI model for analysis
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Credits Used</p>
                <p className="font-outfit font-bold text-[#0B3D91]">
                  {creditsUsed.toLocaleString()}
                  <span className="text-slate-400 font-normal">
                    {creditsLimit === -1 ? ' / ∞' : ` / ${creditsLimit.toLocaleString()}`}
                  </span>
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {orgPolicy?.restrict_models && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800" data-testid="org-model-policy-notice">
                  <Lock className="w-4 h-4 flex-shrink-0" />
                  <span>Your organization restricts available models. Contact your admin to change policy.</span>
                </div>
              )}
              {models.map((model) => {
                const isSelected = currentModel === model.id;
                const isRestricted = orgPolicy?.restrict_models && !(orgPolicy.allowed_models || []).includes(model.id);
                return (
                  <button
                    key={model.id}
                    onClick={() => !isRestricted && handleModelChange(model.id)}
                    disabled={modelLoading || isRestricted}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      isRestricted ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed' :
                      isSelected 
                        ? 'border-[#0B3D91] bg-[#0B3D91]/5' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`model-select-${model.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getModelColor(model.id)} flex items-center justify-center text-white`}>
                          {getModelIcon(model.id)}
                        </div>
                        <div>
                          <p className={`font-semibold ${isSelected ? 'text-[#0B3D91]' : 'text-slate-700'}`}>
                            {model.name}
                          </p>
                          <p className="text-sm text-slate-500">{model.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          model.multiplier === 1 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : model.multiplier <= 30 
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-rose-100 text-rose-700'
                        }`}>
                          {model.multiplier}x credits
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-[#0B3D91] flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-600">
                <strong>{t('settings.howCreditsWork')}</strong> {t('settings.creditsExplanation')}
              </p>
            </div>
          </div>

          {/* Language Section */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 mb-6">
            <div className="mb-6">
              <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]">
                {t('settings.language')}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t('settings.languageSubtitle')}
              </p>
            </div>

            <div className="space-y-3">
              {availableLanguages.map((lang) => {
                const isSelected = language === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      isSelected 
                        ? 'border-[#0B3D91] bg-[#0B3D91]/5' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`language-select-${lang.code}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-2xl">
                          {lang.flag}
                        </div>
                        <div>
                          <p className={`font-semibold ${isSelected ? 'text-[#0B3D91]' : 'text-slate-700'}`}>
                            {lang.name}
                          </p>
                          <p className="text-sm text-slate-500">{lang.nativeName}</p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-[#0B3D91] flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Profile Section */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 mb-6">
            <h2 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-6">
              Profile Information
            </h2>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#DCEBFF] rounded-full flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-[#0B3D91]" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-700">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-12 py-5 border-slate-200 focus:ring-[#1E5EFF]"
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="pl-12 py-5 bg-slate-50 border-slate-200"
                    data-testid="email-input"
                  />
                </div>
                <p className="text-xs text-slate-400">Email cannot be changed</p>
              </div>
            </div>

            <Button
              onClick={handleSave}
              className="mt-6 bg-[#0B3D91] hover:bg-[#082A66] text-white rounded-xl"
              disabled={loading}
              data-testid="save-settings-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              Save Changes
            </Button>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 mb-6">
            <h2 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4">
              Account Details
            </h2>

            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                <span className="text-slate-600">Subscription</span>
                <span className="font-medium text-[#0B3D91] capitalize">
                  {user?.subscription_tier || 'Starter'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                <span className="text-slate-600">SKU Limit</span>
                <span className="font-medium text-slate-700">
                  {user?.sku_limit?.toLocaleString() || '200'} SKUs
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-slate-600">Total Analyses</span>
                <span className="font-medium text-slate-700">
                  {user?.analyses_count || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          {notifPrefs && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100" data-testid="notification-preferences">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]">Notification Preferences</h2>
                  <p className="text-sm text-slate-500">Choose what you want to be notified about</p>
                </div>
              </div>

              {/* Global Channels */}
              <div className="flex gap-4 mb-5 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3 flex-1">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Email Notifications</span>
                  <button
                    onClick={() => toggleNotifChannel('email_enabled', !notifPrefs.email_enabled)}
                    disabled={notifLoading}
                    className={`ml-auto relative w-10 h-5 rounded-full transition-colors ${notifPrefs.email_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    data-testid="email-notifications-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifPrefs.email_enabled ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                <div className="w-px bg-slate-200" />
                <div className="flex items-center gap-3 flex-1">
                  <Bell className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">In-App Notifications</span>
                  <button
                    onClick={() => toggleNotifChannel('in_app_enabled', !notifPrefs.in_app_enabled)}
                    disabled={notifLoading}
                    className={`ml-auto relative w-10 h-5 rounded-full transition-colors ${notifPrefs.in_app_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    data-testid="inapp-notifications-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifPrefs.in_app_enabled ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Individual Prefs */}
              <div className="divide-y divide-slate-100">
                {Object.entries(notifPrefs.definitions || {}).map(([key, def]) => {
                  const isOn = notifPrefs.preferences[key] ?? def.default;
                  return (
                    <div key={key} className="flex items-center justify-between py-3" data-testid={`notif-pref-${key}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{def.label}</p>
                        <p className="text-xs text-slate-500">{def.description}</p>
                      </div>
                      <button
                        onClick={() => updateNotifPref(key, !isOn)}
                        disabled={notifLoading}
                        className={`relative w-10 h-5 rounded-full transition-colors ${isOn ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        data-testid={`notif-toggle-${key}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isOn ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Settings;
